/**
 * Sandbox File Artifacts
 *
 * Shared helpers for exposing Losi Computer files as authenticated app URLs.
 *
 * Primary responsibilities:
 * - Validate sandbox file route inputs
 * - Build same-origin preview and download URLs with explicit publish levels
 * - Infer file names, languages, and MIME types from paths
 *
 * Dependencies:
 * - None
 */

export type SandboxFileLinks = {
  publishLevel: SandboxFilePublishLevel
  previewUrl: string
  downloadUrl: string
}

export type SandboxFilePublishLevel = 'private' | 'workspace'

const LOSI_SANDBOX_NAME_PATTERN =
  /^losi-(?:code|computer-use|browser|runner|sandbox)(?:-[a-z0-9]+)*-[a-f0-9]{10}$/

const TEXT_EXTENSIONS = new Set([
  'bash',
  'c',
  'conf',
  'cpp',
  'cjs',
  'css',
  'csv',
  'env',
  'html',
  'js',
  'json',
  'jsx',
  'log',
  'md',
  'mdx',
  'mjs',
  'py',
  'sh',
  'sql',
  'svg',
  'ts',
  'tsx',
  'txt',
  'xml',
  'yaml',
  'yml'
])

// --------------

export function sanitizeLosiSandboxSegment(value: unknown) {
  if (typeof value !== 'string') return null

  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')

  return normalized || null
}

// --------------

export function canAccessLosiSandboxName(sandboxName: string, userId: string) {
  const normalizedSandboxName = sanitizeLosiSandboxSegment(sandboxName)
  const normalizedUserId = sanitizeLosiSandboxSegment(userId)

  return Boolean(
    normalizedSandboxName &&
      normalizedUserId &&
      normalizedSandboxName === sandboxName.trim().toLowerCase() &&
      LOSI_SANDBOX_NAME_PATTERN.test(normalizedSandboxName) &&
      normalizedSandboxName.includes(normalizedUserId)
  )
}

// --------------

export function isValidSandboxFilePath(path: string | null) {
  return Boolean(path && path.length <= 1024 && !path.includes('\0'))
}

// --------------

export function normalizeSandboxPublishLevel(
  value: unknown
): SandboxFilePublishLevel {
  return value === 'workspace' ? 'workspace' : 'private'
}

// --------------

export function getSandboxFileRouteUrl(input: {
  accessToken?: string | null
  sandboxName: string
  path: string
  publishLevel?: SandboxFilePublishLevel
  workspaceId?: string | null
  download?: boolean
}) {
  const publishLevel = normalizeSandboxPublishLevel(input.publishLevel)
  const params = new URLSearchParams({
    publishLevel,
    sandbox: input.sandboxName,
    path: input.path
  })

  if (publishLevel === 'workspace' && input.workspaceId) {
    params.set('workspaceId', input.workspaceId)
  }

  if (input.accessToken) {
    params.set('token', input.accessToken)
  }

  if (input.download) {
    params.set('download', '1')
  }

  return `/api/sandbox/files?${params.toString()}`
}

// --------------

export function getSandboxFileLinks(
  sandboxName: string | null | undefined,
  path: string | null | undefined,
  options: {
    accessToken?: string | null
    publishLevel?: SandboxFilePublishLevel | null
    workspaceId?: string | null
  } = {}
): SandboxFileLinks | null {
  if (!sandboxName || !path || !isValidSandboxFilePath(path)) return null

  const publishLevel = normalizeSandboxPublishLevel(options.publishLevel)

  return {
    publishLevel,
    previewUrl: getSandboxFileRouteUrl({
      sandboxName,
      path,
      accessToken: options.accessToken,
      publishLevel,
      workspaceId: options.workspaceId
    }),
    downloadUrl: getSandboxFileRouteUrl({
      sandboxName,
      path,
      accessToken: options.accessToken,
      publishLevel,
      workspaceId: options.workspaceId,
      download: true
    })
  }
}

// --------------

export function getSandboxFileName(path: string) {
  const normalizedPath = path.replace(/\\/g, '/').replace(/\/+$/g, '')
  const fileName = normalizedPath.split('/').filter(Boolean).pop()

  return fileName || 'artifact'
}

// --------------

export function getSandboxFileExtension(path: string) {
  const fileName = getSandboxFileName(path)
  const extension = fileName.split('.').pop()?.toLowerCase()

  return extension && extension !== fileName.toLowerCase() ? extension : ''
}

// --------------

export function getSandboxFileLanguage(path: string) {
  switch (getSandboxFileExtension(path)) {
    case 'ts':
    case 'tsx':
      return 'tsx'
    case 'js':
    case 'jsx':
    case 'mjs':
    case 'cjs':
      return 'jsx'
    case 'json':
      return 'json'
    case 'css':
      return 'css'
    case 'html':
    case 'htm':
      return 'html'
    case 'md':
    case 'mdx':
      return 'markdown'
    case 'py':
      return 'python'
    case 'sh':
    case 'bash':
      return 'bash'
    case 'csv':
      return 'csv'
    case 'pdf':
      return 'pdf'
    default:
      return 'text'
  }
}

// --------------

export function getSandboxFileMimeType(path: string) {
  switch (getSandboxFileExtension(path)) {
    case 'css':
      return 'text/css; charset=utf-8'
    case 'csv':
      return 'text/csv; charset=utf-8'
    case 'gif':
      return 'image/gif'
    case 'html':
    case 'htm':
      return 'text/html; charset=utf-8'
    case 'jpeg':
    case 'jpg':
      return 'image/jpeg'
    case 'js':
    case 'mjs':
    case 'cjs':
      return 'text/javascript; charset=utf-8'
    case 'json':
      return 'application/json; charset=utf-8'
    case 'md':
    case 'mdx':
      return 'text/markdown; charset=utf-8'
    case 'mp3':
      return 'audio/mpeg'
    case 'mp4':
      return 'video/mp4'
    case 'pdf':
      return 'application/pdf'
    case 'png':
      return 'image/png'
    case 'py':
    case 'sh':
    case 'bash':
    case 'txt':
    case 'log':
    case 'env':
      return 'text/plain; charset=utf-8'
    case 'svg':
      return 'image/svg+xml'
    case 'ts':
    case 'tsx':
      return 'text/typescript; charset=utf-8'
    case 'wav':
      return 'audio/wav'
    case 'webm':
      return 'video/webm'
    case 'webp':
      return 'image/webp'
    case 'xml':
      return 'application/xml; charset=utf-8'
    case 'zip':
      return 'application/zip'
    default:
      return 'application/octet-stream'
  }
}

// --------------

export function isTextSandboxFile(path: string, mimeType?: string | null) {
  if (mimeType?.startsWith('text/')) return true
  if (mimeType?.includes('json') || mimeType?.includes('xml')) return true

  return TEXT_EXTENSIONS.has(getSandboxFileExtension(path))
}

// --------------

export function isPdfSandboxFile(path: string, mimeType?: string | null) {
  return mimeType?.startsWith('application/pdf') || getSandboxFileExtension(path) === 'pdf'
}
